import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "@/components/layouts/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useUpdateUserProfile, useUserDetails } from "@/hooks/api/useUserDetails";
import Step1UserInfo from "@/components/onboarding/Step1UserInfo";
import Step2ContactImport from "@/components/onboarding/Step2ContactImport";
import Step3ContactManager from "@/components/onboarding/Step3ContactManager";

const Onboarding = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [backendStep, setBackendStep] = useState(1);
  const updateProfile = useUpdateUserProfile();
  const { data: userDetails, isLoading: isLoadingUser } = useUserDetails();

  // Handle initial step setup from URL and user data
  useEffect(() => {
    if (userDetails) {
      // Set backend step from user data
      setBackendStep(userDetails.onboarding_step || 1);
      
      // Get step from URL
      const stepParam = searchParams.get('step');
      const requestedStep = stepParam ? parseInt(stepParam) : userDetails.onboarding_step || 1;
      
      // Validate that user can access requested step
      const validStep = Math.min(requestedStep, userDetails.onboarding_step || 1);
      setCurrentStep(validStep);
      
      // Update URL if needed
      if (stepParam && parseInt(stepParam) !== validStep) {
        setSearchParams({ step: validStep.toString() });
      }
    }
  }, [userDetails, searchParams, setSearchParams]);

  const handleStep1Submit = async (values: { name: string; country: string; phoneNumber?: string }) => {
    try {
      await updateProfile.mutateAsync({
        name: values.name,
        country: values.country,
        phone: values.phoneNumber || undefined,
        onboarding_step: 2, // Update to next step
      });
      
      // Move to next step
      setCurrentStep(2);
      setBackendStep(2);
      setSearchParams({ step: '2' });
    } catch (error) {
      // Error is already handled by the mutation hook
      console.error("Failed to update profile:", error);
    }
  };

  const handleStep3Complete = async () => {
    try {
      // Set step to 4 which triggers completedOnboarding in backend
      await updateProfile.mutateAsync({
        onboarding_step: 4,
      });
      
      // Show success toast
      toast({
        title: "Welcome to Wabisabi!",
        description: "Your profile has been set up successfully.",
      });
      
      // Navigate to dashboard after successful update
      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
    }
  };

  const handleStepNavigation = async (nextStep: number) => {
    // Only allow moving to the next step if it's not beyond backend step
    if (nextStep > backendStep + 1) {
      toast({
        title: "Please complete previous steps first",
        variant: "destructive",
      });
      return;
    }

    // If moving forward, update backend step
    if (nextStep > backendStep) {
      try {
        await updateProfile.mutateAsync({
          onboarding_step: nextStep,
        });
        setBackendStep(nextStep);
      } catch (error) {
        console.error("Failed to update step:", error);
        return;
      }
    }

    setCurrentStep(nextStep);
    setSearchParams({ step: nextStep.toString() });
  };

  // Show loading state while fetching user data
  if (isLoadingUser) {
    return (
      <AuthLayout>
        <div className="flex min-h-[calc(100vh-80px)] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </AuthLayout>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1UserInfo 
            onSubmit={handleStep1Submit}
            isLoading={updateProfile.isPending}
          />
        );

      case 2:
        return (
          <Step2ContactImport
            onBack={() => handleStepNavigation(1)}
            onContinue={() => handleStepNavigation(3)}
          />
        );

      case 3:
        return (
          <Step3ContactManager
            onBack={() => handleStepNavigation(2)}
            onComplete={handleStep3Complete}
            isLoading={updateProfile.isPending}
          />
        );

      default:
        return null;
    }
  };

  const progressBar = (
    <div className="w-full h-[2px] bg-gray-200">
      <div 
        className="h-full bg-[#6C4F77] transition-all duration-300"
        style={{ width: `${(currentStep / 3) * 100}%` }}
      />
    </div>
  );

  return (
    <AuthLayout headerBottomComponent={progressBar}>
      <div className="flex flex-col min-h-[calc(100vh-80px)]">
        <div className="flex-1">
          {renderStep()}
        </div>
      </div>
    </AuthLayout>
  );
};

export default Onboarding;